import {
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { mockAuthService } from './auth.mock';

const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN;
const redirectUri = `${window.location.origin}/auth/callback`;
const useMockAuth = import.meta.env.VITE_USE_MOCK_AUTH === 'true';

// Token storage key
const TOKEN_STORAGE_KEY = 'cognitoTokens';

// Logout flag to prevent auto-login after intentional logout
const LOGOUT_FLAG_KEY = 'intentionalLogout';

// Helper to get stored tokens
function getStoredTokens(): { idToken: string; accessToken: string; refreshToken: string; expiresAt: number } | null {
  const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

// Helper to decode JWT payload
function decodeJwtPayload(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

// Use mock auth service if enabled
const baseAuthService = useMockAuth ? mockAuthService : {
  // Get the Cognito hosted UI login URL
  getLoginUrl(): string {
    const params = new URLSearchParams({
      client_id: clientId,
      response_type: 'code',
      scope: 'email openid profile',
      redirect_uri: redirectUri,
      identity_provider: 'GoogleWorkspace', // Skip hosted UI, go directly to IdP
    });
    return `https://${cognitoDomain}/oauth2/authorize?${params.toString()}`;
  },

  // Exchange authorization code for tokens
  async exchangeCodeForTokens(code: string): Promise<boolean> {
    try {
      const tokenEndpoint = `https://${cognitoDomain}/oauth2/token`;

      const params = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: clientId,
        code: code,
        redirect_uri: redirectUri,
      });

      const response = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Token exchange failed:', errorText);
        return false;
      }

      const tokens = await response.json();

      // Store tokens in localStorage
      localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify({
        idToken: tokens.id_token,
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        expiresAt: Date.now() + (tokens.expires_in * 1000),
      }));

      return true;
    } catch (error) {
      console.error('Error exchanging code for tokens:', error);
      return false;
    }
  },

  // Get the logout URL (redirects to our logout page after Cognito logout)
  getLogoutUrl(): string {
    const params = new URLSearchParams({
      client_id: clientId,
      logout_uri: `${window.location.origin}/logout`,
    });
    return `https://${cognitoDomain}/logout?${params.toString()}`;
  },

  // Redirect to Cognito login
  login(): void {
    window.location.href = this.getLoginUrl();
  },

  // Logout and redirect through Cognito logout endpoint
  logout(): void {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    // Set flag to prevent auto-login when landing on logout page
    sessionStorage.setItem(LOGOUT_FLAG_KEY, 'true');
    // Redirect through Cognito logout to properly clear the session
    window.location.href = this.getLogoutUrl();
  },

  // Check if user intentionally logged out (to prevent auto-login)
  hasIntentionallyLoggedOut(): boolean {
    return sessionStorage.getItem(LOGOUT_FLAG_KEY) === 'true';
  },

  // Clear the intentional logout flag (call this when user clicks "Sign In Again")
  clearLogoutFlag(): void {
    sessionStorage.removeItem(LOGOUT_FLAG_KEY);
  },

  // Get token expiry time in milliseconds (returns null if not authenticated)
  getTokenExpiryTime(): number | null {
    const tokens = getStoredTokens();
    if (!tokens) return null;
    return tokens.expiresAt;
  },

  // Get current session from stored tokens (no API call)
  getCurrentSession(): Promise<CognitoUserSession | null> {
    return new Promise((resolve) => {
      const tokens = getStoredTokens();

      if (!tokens) {
        resolve(null);
        return;
      }

      // Check if tokens are expired
      if (tokens.expiresAt < Date.now()) {
        // Tokens expired, clear them
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        resolve(null);
        return;
      }

      try {
        const idToken = new CognitoIdToken({ IdToken: tokens.idToken });
        const accessToken = new CognitoAccessToken({ AccessToken: tokens.accessToken });
        const refreshToken = new CognitoRefreshToken({ RefreshToken: tokens.refreshToken });

        const session = new CognitoUserSession({
          IdToken: idToken,
          AccessToken: accessToken,
          RefreshToken: refreshToken,
        });

        resolve(session);
      } catch (error) {
        console.error('Error creating session from stored tokens:', error);
        resolve(null);
      }
    });
  },

  // Get current user from stored tokens (no API call)
  getCurrentUser(): Promise<any> {
    return new Promise((resolve) => {
      const tokens = getStoredTokens();

      if (!tokens) {
        resolve(null);
        return;
      }

      // Check if tokens are expired
      if (tokens.expiresAt < Date.now()) {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        resolve(null);
        return;
      }

      // Decode the ID token to get user attributes
      const payload = decodeJwtPayload(tokens.idToken);
      if (!payload) {
        resolve(null);
        return;
      }

      resolve({
        username: payload['cognito:username'] || payload['sub'] || payload['email'],
        attributes: {
          email: payload['email'],
          email_verified: payload['email_verified'],
          given_name: payload['given_name'],
          family_name: payload['family_name'],
          'custom:groups': payload['custom:groups'],
        },
      });
    });
  },

  // Get ID token for API calls
  async getIdToken(): Promise<string | null> {
    const tokens = getStoredTokens();
    if (!tokens) return null;

    // Check if expired
    if (tokens.expiresAt < Date.now()) {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      return null;
    }

    return tokens.idToken;
  },

  // Check if user is authenticated
  async isAuthenticated(): Promise<boolean> {
    const tokens = getStoredTokens();
    if (!tokens) return false;

    // Check if expired
    if (tokens.expiresAt < Date.now()) {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      return false;
    }

    return true;
  },

  // Refresh session using refresh token
  async refreshSession(): Promise<CognitoUserSession | null> {
    const tokens = getStoredTokens();
    if (!tokens || !tokens.refreshToken) {
      return null;
    }

    try {
      const tokenEndpoint = `https://${cognitoDomain}/oauth2/token`;

      const params = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: clientId,
        refresh_token: tokens.refreshToken,
      });

      const response = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      if (!response.ok) {
        console.error('Token refresh failed');
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        return null;
      }

      const newTokens = await response.json();

      // Store new tokens
      localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify({
        idToken: newTokens.id_token,
        accessToken: newTokens.access_token,
        refreshToken: tokens.refreshToken, // Refresh token doesn't change
        expiresAt: Date.now() + (newTokens.expires_in * 1000),
      }));

      const idToken = new CognitoIdToken({ IdToken: newTokens.id_token });
      const accessToken = new CognitoAccessToken({ AccessToken: newTokens.access_token });
      const refreshToken = new CognitoRefreshToken({ RefreshToken: tokens.refreshToken });

      return new CognitoUserSession({
        IdToken: idToken,
        AccessToken: accessToken,
        RefreshToken: refreshToken,
      });
    } catch (error) {
      console.error('Error refreshing session:', error);
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      return null;
    }
  },
};

// Export the appropriate auth service based on environment
export const authService = baseAuthService;
